"use client";

import { LandingCTA, LandingFooter, LandingNavbar, LandingPricing } from "@/components/landing";
import { languageData } from "@/data/languageData";
import { ArrowRight, Globe, Mic } from "lucide-react";
import Link from "next/link";

const regionIcons: Record<string, string> = {
  Europe: "🇪🇺",
  Asia: "🌏",
  Americas: "🌎",
  Oceania: "🌊",
  "Africa & Middle East": "🌍",
};

export default function LanguagesPage() {
  const totalLanguages = Object.values(languageData).flat().length;

  return (
    <main className="min-h-screen bg-[#F5F5F0]">
      <LandingNavbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-12 pb-20 lg:pt-24 lg:pb-32">
        {/* Decorative Background Elements similar to LandingHero */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-64 h-64 bg-yellow-200/40 rounded-full blur-3xl opacity-60" />
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-orange-200/40 rounded-full blur-3xl opacity-60" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-full text-orange-700 font-bold mb-8 border border-orange-200 shadow-sm">
            <Globe className="w-5 h-5" />
            <span className="uppercase tracking-wide text-xs md:text-sm">Global Coverage</span>
          </div>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-gray-900 mb-8 leading-tight tracking-tight">
            70+ languages <span className="text-orange-600">available</span>
            <br />
          </h1>

          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-12 leading-relaxed">
            Choose from our massive library of supported languages.
            <br className="hidden md:block" />
            From Spanish to Swahili, our AI tutor is ready to chat 24/7.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              href="https://chickytutor.com/auth"
              className="w-full sm:w-auto px-8 py-4 bg-gray-900 text-white font-bold text-lg rounded-xl hover:bg-gray-800 transition-all transform hover:scale-105 shadow-xl flex items-center justify-center gap-2 group"
            >
              Start Speaking Now
              <Mic className="w-5 h-5 group-hover:text-orange-400 transition-colors" />
            </Link>
            <Link
              href="/landing#pricing"
              className="w-full sm:w-auto px-8 py-4 bg-white text-gray-900 font-bold text-lg rounded-xl border-2 border-gray-200 hover:border-gray-900 transition-all flex items-center justify-center gap-2"
            >
              View Pricing
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Languages by Region */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid gap-12">
            {Object.entries(languageData).map(([region, languages]) => (
              <div
                key={region}
                className="bg-white rounded-3xl p-8 md:p-10 shadow-lg shadow-orange-500/5 border border-gray-100"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4 mb-8">
                  <div className="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center text-4xl shadow-sm">
                    {regionIcons[region]}
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900">{region}</h2>
                    <p className="text-gray-500 font-medium">
                      {languages.length} languages available
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
                  {languages.map((language) => (
                    <Link
                      href={`/landing?language=${encodeURIComponent(language)}`}
                      key={language}
                      className="group flex items-center gap-3 p-4 rounded-xl border border-gray-100 bg-gray-50/50 hover:bg-white hover:border-orange-200 hover:shadow-md transition-all duration-200"
                    >
                      <div className="w-2 h-2 rounded-full bg-gray-300 group-hover:bg-green-500 transition-colors" />
                      <span className="font-bold text-gray-700 group-hover:text-gray-900">
                        {language}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <LandingPricing />

      <LandingCTA />
      <LandingFooter />
    </main>
  );
}
